import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';
import { DateTime } from 'luxon';
import type { User } from '@getaccept/lib-shared-new/src/users/types/user';
import type { Participant } from '../../types/participant';
import { CommentsHelper } from '../helpers/comments.helper';
import { ReadEventsHelper } from '../helpers/read-events.helper';
import type { Comment, Reply } from '../types/comment';
import type { CommentReadEvent } from '../types/comment-read-event';
import type { CurrentUser } from '../types/current-user';

export function useReadEvents(
  comment: Ref<Comment>,
  participants: Participant[],
  sender: User,
  currentUser: Ref<CurrentUser>
) {
  const senderRecipientsParticipants = computed(() =>
    ReadEventsHelper.filterOutParticipant(
      [...participants.map(({ id }) => id), sender.id],
      currentUser.value.participantId
    )
  );

  const mappedLatestReadEvents: ComputedRef<{ [id: string]: string[] }> = computed(() => {
    const { reads, replies, id } = comment.value;

    return senderRecipientsParticipants.value.reduce(
      (acc: { [id: string]: string[] }, participantId) => {
        const commentReadEvent = ReadEventsHelper.getReadEvent(reads, participantId);
        if (!commentReadEvent) {
          return acc;
        }

        const latestReadReply = replies.find((reply: Reply, index: number) => {
          const currentReplyHasBeenRead = CommentsHelper.isReadByParticipant(
            reply.reads,
            participantId
          );

          if (!currentReplyHasBeenRead) {
            return false;
          }
          if (ReadEventsHelper.isLastReply(comment.value, index)) {
            return true;
          }
          const isNextReplyRead = CommentsHelper.isReadByParticipant(
            replies[index + 1].reads,
            participantId
          );
          return !isNextReplyRead;
        });

        if (!latestReadReply) {
          acc[id] = [
            ...(acc[id] || []),
            getUserMetadata(participantId, commentReadEvent.timestamp),
          ];
          return acc;
        }
        const readEvent: CommentReadEvent = ReadEventsHelper.getReadEvent(
          latestReadReply.reads,
          participantId
        );

        acc[latestReadReply.id] = [
          ...(acc[latestReadReply.id] || []),
          getUserMetadata(participantId, readEvent.timestamp),
        ];
        return acc;
      },
      {}
    );
  });

  const getUserMetadata = (participantId: string, timestamp: number) =>
    `${
      CommentsHelper.getUserMetaData(participantId, [...participants, sender as Participant]).name
    }・${DateTime.fromMillis(timestamp).toLocaleString({
      ...DateTime.TIME_SIMPLE,
    })}`;

  return { mappedLatestReadEvents };
}
